import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowRight, DollarSign, CreditCard, Truck, BarChart3, 
  Cog, Banknote, TrendingUp, Shield, CheckCircle
} from 'lucide-react';

const FinancingPage = () => {
  const financingOptions = [
    {
      title: "Working Capital Line of Credit",
      description: "Flexible revolving credit for daily operational expenses. Draw funds as needed, repay, and reuse.",
      icon: CreditCard,
      link: "/financing/working-capital",
      color: "emerald"
    },
    {
      title: "Term Loans",
      description: "Structured financing for growth initiatives with fixed payments and predictable repayment schedules.",
      icon: Banknote,
      link: "/financing/term-loans",
      color: "blue"
    },
    {
      title: "Equipment Financing",
      description: "Finance commercial mowers, trucks, trailers, and all the equipment your business needs to grow.",
      icon: Truck,
      link: "/financing/equipment",
      color: "amber"
    }
  ];

  const ecosystemFeatures = [
    {
      icon: Cog,
      title: "Software",
      description: "Manage your entire operation—customers, properties, crews, scheduling, and invoicing."
    },
    {
      icon: DollarSign,
      title: "Payments",
      description: "Process payments and generate the revenue data that powers your financing options."
    },
    {
      icon: TrendingUp,
      title: "Financing",
      description: "Access capital solutions based on your real business performance and payment history."
    }
  ];

  const benefits = [
    "Manage operations in one platform",
    "Process payments seamlessly",
    "Access financing when you need it",
    "Scale your business with confidence"
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              Business Financing Built for Landscaping Companies
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Access working capital, equipment financing, and growth funding directly within your landscaping management system.
            </p>
            <Link to="/apply">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Apply Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Complete Ecosystem */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">A Complete Ecosystem for Your Business</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Orchard LMS provides everything landscaping companies need to manage operations, process payments, access financing, and scale their business—all in one integrated platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold text-gray-900">Free Platform Access</h3>
              </div>
              <p className="text-gray-600">
                The landscaping management system is <strong>completely free</strong> for clients using our integrated payment processing. Financing is available as an additional feature to help you grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Financing Options</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the financing solution that fits your business needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {financingOptions.map((option, index) => (
              <Card key={index} className="border-gray-100 hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-8">
                  <div className={`w-14 h-14 bg-${option.color}-100 rounded-xl flex items-center justify-center mb-6`}>
                    <option.icon className={`h-7 w-7 text-${option.color}-600`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{option.title}</h3>
                  <p className="text-gray-600 mb-6">{option.description}</p>
                  <Link to={option.link}>
                    <Button variant="outline" className="w-full">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Software + Payments + Financing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Software + Payments + Financing</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our integrated ecosystem creates a powerful cycle: the system manages your operations, payments generate revenue data, and that data unlocks financing solutions tailored to your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {ecosystemFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-12 gap-4">
            <div className="flex items-center gap-2 text-gray-500">
              <div className="w-16 h-1 bg-gray-200 rounded"></div>
              <ArrowRight className="h-5 w-5" />
              <div className="w-16 h-1 bg-gray-200 rounded"></div>
              <ArrowRight className="h-5 w-5" />
              <div className="w-16 h-1 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Fuel Your Growth?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Apply for financing and get the capital you need to grow your landscaping business.
          </p>
          <Link to="/apply">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default FinancingPage;
